import React from 'react'
import { ShimmerBlock } from './Shimmer'

/**
 * Stands in for HomeHighlight while the feed loads and while the feature's own
 * chunk arrives. Written in Tailwind rather than the feature's classes so it can
 * be imported from ViewFeed without dragging HomeHighlight.scss into the main
 * bundle — the frame below mirrors what that stylesheet paints.
 */

const CARD = 'bg-surface-2 border border-Neutral-3 rounded-[8px]'

/** Matches the three rows the carousel lists for a market. */
const OUTCOME_ROWS = [0, 1, 2]

const PanelSkeleton = ({ rows }) => (
  <div className={`${CARD} p-5 md:p-6`}>
    <div className="flex items-center justify-between gap-3 mb-4">
      <ShimmerBlock className="h-5 w-40 rounded" />
      <ShimmerBlock className="h-8 w-8 rounded-[8px]" />
    </div>

    <div className="flex flex-col gap-4">
      {rows.map(row => (
        <div key={row} className="flex items-center gap-3">
          <ShimmerBlock className="h-8 w-8 rounded-full shrink-0" />
          <ShimmerBlock className="h-4 flex-1 rounded" />
          <ShimmerBlock className="h-4 w-16 shrink-0 rounded" />
        </div>
      ))}
    </div>
  </div>
)

/* The comments panel is pinned to 202px and scrolls, so the stand-in holds that
   height and shows only the rows that fit. */
const CommentsPanelSkeleton = () => (
  <div className={`${CARD} p-4 h-[202px] overflow-hidden`}>
    <div className="flex items-center gap-3 mb-3">
      <ShimmerBlock className="h-5 w-40 rounded" />
    </div>

    <div className="flex flex-col gap-1">
      {[0, 1, 2].map(row => (
        <div key={row} className="flex items-start gap-2 px-2 py-1.5">
          <ShimmerBlock className="h-10 w-10 rounded-full shrink-0" />
          <div className="flex flex-col gap-1 flex-1">
            <ShimmerBlock className="h-[18px] w-40 rounded" />
            <ShimmerBlock className="h-[18px] w-full rounded" />
          </div>
        </div>
      ))}
    </div>
  </div>
)

const HomeHighlightSkeleton = () => (
  <div
    className="grid grid-cols-1 gap-4 px-4 pb-6 lg:grid-cols-[minmax(0,2.2fr)_minmax(300px,1fr)] lg:gap-6 lg:px-[30px]"
    aria-hidden="true">
    <section className={`${CARD} p-5 md:p-6`}>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] md:gap-8">
        <div className="flex flex-col">
          <ShimmerBlock className="h-7 w-4/5 rounded" />
          <ShimmerBlock className="h-7 w-3/5 mt-2 rounded" />
          <ShimmerBlock className="h-4 w-24 mt-3 rounded" />

          <div className="flex flex-col gap-3 mt-5">
            {OUTCOME_ROWS.map(row => (
              <ShimmerBlock key={row} className="h-11 w-full rounded-[8px]" />
            ))}
          </div>

          <ShimmerBlock className="h-4 w-full mt-6 rounded" />
          <ShimmerBlock className="h-4 w-11/12 mt-2 rounded" />
        </div>

        <div className="flex flex-col">
          <div className="flex flex-wrap gap-x-5 gap-y-2 mb-4">
            {[0, 1].map(item => (
              <div key={item} className="flex items-center gap-2">
                <ShimmerBlock className="h-2 w-2 rounded-full" />
                <ShimmerBlock className="h-4 w-24 rounded" />
              </div>
            ))}
          </div>

          {/* Holds the same footprint the market chart's canvas takes. */}
          <ShimmerBlock className="h-[268px] w-full rounded-[8px]" />
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 mt-4">
        <ShimmerBlock className="h-8 w-8 rounded-[8px]" />
        <ShimmerBlock className="h-4 w-12 rounded" />
        <ShimmerBlock className="h-8 w-8 rounded-[8px]" />
      </div>
    </section>

    <div className="flex flex-col gap-4 lg:gap-6">
      <PanelSkeleton rows={[0, 1, 2]} />
      <CommentsPanelSkeleton />
    </div>
  </div>
)

export default HomeHighlightSkeleton
